import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../api/ApiService';
import LocalDataService from '../../api/LocalDataService';
import './PointsRechargePage.css';

const POINTS_SHOP_TYPE = 10;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const unwrapPayload = (response) => response?.data ?? response;

const getPackagePrice = (pkg) => {
  const yuan = Number(pkg?.money_yuan);
  if (Number.isFinite(yuan)) return yuan;
  const cents = Number(pkg?.money);
  return Number.isFinite(cents) ? cents / 100 : 0;
};

const formatPrice = (value) => {
  const price = Number(value) || 0;
  return Number.isInteger(price) ? price.toFixed(0) : price.toFixed(2);
};

const formatPoints = (value) => (Number(value) || 0).toLocaleString('zh-CN', {
  maximumFractionDigits: 4
});

const getErrorMessage = (error, fallback) => {
  try {
    const raw = typeof error?.data === 'string' ? JSON.parse(error.data) : error?.data;
    return raw?.detail || raw?.message || fallback;
  } catch (_) {
    return fallback;
  }
};

function PaymentMark({ method }) {
  if (method === 'alipay') {
    return <span className="canvas-recharge-method-mark is-alipay" aria-hidden="true">支</span>;
  }
  return <span className="canvas-recharge-method-mark is-wechat" aria-hidden="true">微</span>;
}

function PointsRechargePage() {
  const history = useHistory();
  const user = LocalDataService.load_user_data();
  const token = user?.token;
  const apiRef = useRef(new ApiService());
  const pollTimerRef = useRef(null);
  const pollDeadlineRef = useRef(0);
  const requestSequenceRef = useRef(0);

  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [payMethod, setPayMethod] = useState('wechat');
  const [pointsBalance, setPointsBalance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [orderPrice, setOrderPrice] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle');

  const clearPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const refreshBalance = useCallback(async () => {
    if (!token) return null;
    try {
      const result = await apiRef.current.get_user_money(token, { silent: true });
      const nextBalance = Number(unwrapPayload(result)?.points_balance);
      if (!Number.isFinite(nextBalance)) return null;
      setPointsBalance(nextBalance);
      window.dispatchEvent(new CustomEvent('canvas:balance-updated', {
        detail: { pointsBalance: nextBalance }
      }));
      return nextBalance;
    } catch (_) {
      return null;
    }
  }, [token]);

  const finishPayment = useCallback(async () => {
    clearPolling();
    setPaymentStatus('success');
    await refreshBalance();
    toast.success('充值成功，积分已到账');
  }, [clearPolling, refreshBalance]);

  const pollOrder = useCallback((tradeNo, requestSequence) => {
    const check = async () => {
      if (requestSequenceRef.current !== requestSequence) return;
      if (Date.now() >= pollDeadlineRef.current) {
        setPaymentStatus('expired');
        return;
      }

      try {
        const response = await apiRef.current.get_order_info(token, tradeNo, { silent: true });
        if (requestSequenceRef.current !== requestSequence) return;
        const order = unwrapPayload(response) || {};
        if (Number(order.status) === 2) {
          await finishPayment();
          return;
        }
      } catch (_) {
        // A transient status-query failure should not discard a valid payment QR code.
      }

      if (requestSequenceRef.current === requestSequence) {
        pollTimerRef.current = setTimeout(check, POLL_INTERVAL_MS);
      }
    };

    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    pollTimerRef.current = setTimeout(check, POLL_INTERVAL_MS);
  }, [finishPayment, token]);

  const createOrder = useCallback(async (pkg, method) => {
    if (!token || !pkg) return;
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    clearPolling();
    setIsCreatingOrder(true);
    setQrCode('');
    setOrderNo('');
    setOrderPrice(null);
    setPaymentStatus('creating');

    try {
      const api = apiRef.current;
      const response = method === 'alipay'
        ? await api.create_alipay_order_info(token, pkg.id, { silent: true })
        : await api.create_wx_order_info(token, pkg.id, { silent: true });
      if (requestSequenceRef.current !== requestSequence) return;

      const payload = unwrapPayload(response) || {};
      const tradeNo = payload.out_trade_no || payload.order_info?.out_trade_no;
      const image = payload.img_b64;
      if (!image || !tradeNo) throw new Error('invalid_payment_response');

      const paidCents = Number(payload.order_info?.money);
      setOrderPrice(Number.isFinite(paidCents) ? paidCents / 100 : getPackagePrice(pkg));
      setQrCode(image);
      setOrderNo(tradeNo);
      setPaymentStatus('waiting');
      pollOrder(tradeNo, requestSequence);
    } catch (error) {
      if (requestSequenceRef.current !== requestSequence) return;
      setPaymentStatus('error');
      toast.error(getErrorMessage(error, '付款码生成失败，请稍后重试'));
    } finally {
      if (requestSequenceRef.current === requestSequence) setIsCreatingOrder(false);
    }
  }, [clearPolling, pollOrder, token]);

  const loadRechargeData = useCallback(async () => {
    if (!token) {
      history.replace(`/login?redirect=${encodeURIComponent('/recharge')}`);
      return;
    }

    setIsLoading(true);
    setLoadError('');
    const [packageResult, balanceResult] = await Promise.allSettled([
      apiRef.current.get_pay_info_list(token, POINTS_SHOP_TYPE, { silent: true }),
      apiRef.current.get_user_money(token, { silent: true })
    ]);

    if (balanceResult.status === 'fulfilled') {
      const nextBalance = Number(unwrapPayload(balanceResult.value)?.points_balance);
      if (Number.isFinite(nextBalance)) setPointsBalance(nextBalance);
    }

    if (packageResult.status === 'rejected') {
      setLoadError(getErrorMessage(packageResult.reason, '套餐列表加载失败，请稍后重试'));
      setIsLoading(false);
      return;
    }

    const payload = unwrapPayload(packageResult.value);
    const list = (Array.isArray(payload) ? payload : [])
      .filter((pkg) => pkg && pkg.id)
      .sort((left, right) => getPackagePrice(left) - getPackagePrice(right));
    if (list.length === 0) {
      setLoadError('当前暂无可用充值套餐');
      setIsLoading(false);
      return;
    }

    const recommended = list.find((pkg) => String(pkg.tag || '').includes('推荐'))
      || list[Math.min(2, list.length - 1)];
    setPackages(list);
    setSelectedPackage(recommended);
    setIsLoading(false);
  }, [history, token]);

  useEffect(() => {
    loadRechargeData();
  }, [loadRechargeData]);

  useEffect(() => {
    if (!isLoading && selectedPackage) createOrder(selectedPackage, payMethod);
  }, [createOrder, isLoading, payMethod, selectedPackage]);

  useEffect(() => () => {
    requestSequenceRef.current += 1;
    clearPolling();
  }, [clearPolling]);

  const handlePackageSelect = (pkg) => {
    if (pkg.id === selectedPackage?.id) return;
    setSelectedPackage(pkg);
  };

  const handleMethodSelect = (method) => {
    if (method === payMethod) return;
    setPayMethod(method);
  };

  const originalPrice = selectedPackage ? getPackagePrice(selectedPackage) : 0;
  const payablePrice = orderPrice ?? originalPrice;
  const hasDiscount = orderPrice !== null && orderPrice < originalPrice;
  const qrCodeSrc = qrCode && String(qrCode).startsWith('data:')
    ? qrCode
    : qrCode ? `data:image/png;base64,${qrCode}` : '';

  return (
    <main className="canvas-recharge-page">
      <div className="canvas-recharge-container">
        <nav className="canvas-recharge-breadcrumb" aria-label="页面路径">
          <Link to="/">灵感画布</Link>
          <span aria-hidden="true">/</span>
          <strong>积分充值</strong>
        </nav>

        <header className="canvas-recharge-header">
          <div className="canvas-recharge-heading">
            <span className="canvas-recharge-heading-mark" aria-hidden="true">
              <i /><i /><i />
            </span>
            <div>
              <span className="canvas-recharge-eyebrow">POINTS TOP-UP</span>
              <h1>为下一次创作补充积分</h1>
              <p>充值积分与当前账户实时同步，可用于剧查查全部 AI 创作节点。</p>
            </div>
          </div>
          <div className="canvas-recharge-balance">
            <span>当前可用积分</span>
            <strong>{pointsBalance === null ? '—' : formatPoints(pointsBalance)}</strong>
          </div>
        </header>

        {isLoading && (
          <div className="canvas-recharge-loading" role="status">
            <span className="canvas-recharge-spinner" aria-hidden="true" />
            正在读取充值套餐…
          </div>
        )}

        {!isLoading && loadError && (
          <div className="canvas-recharge-error" role="alert">
            <strong>暂时无法加载充值服务</strong>
            <span>{loadError}</span>
            <button type="button" onClick={loadRechargeData}>重新加载</button>
          </div>
        )}

        {!isLoading && !loadError && (
          <div className="canvas-recharge-layout">
            <section className="canvas-recharge-packages" aria-labelledby="canvas-recharge-package-title">
              <div className="canvas-recharge-section-heading">
                <div>
                  <span>01 / SELECT</span>
                  <h2 id="canvas-recharge-package-title">选择充值积分</h2>
                </div>
                <small>到账后积分永久有效</small>
              </div>

              <div className="canvas-recharge-package-grid">
                {packages.map((pkg) => {
                  const isSelected = selectedPackage?.id === pkg.id;
                  const tag = String(pkg.tag || '');
                  return (
                    <button
                      type="button"
                      className={`canvas-recharge-package${isSelected ? ' is-selected' : ''}`}
                      key={pkg.id}
                      aria-pressed={isSelected}
                      onClick={() => handlePackageSelect(pkg)}
                    >
                      {tag && tag !== '积分充值' && <span className="canvas-recharge-package-tag">{tag}</span>}
                      <span className="canvas-recharge-package-points">{formatPoints(pkg.points_balance)}</span>
                      <span className="canvas-recharge-package-unit">积分</span>
                      <span className="canvas-recharge-package-price">¥ {formatPrice(getPackagePrice(pkg))}</span>
                      <span className="canvas-recharge-package-check" aria-hidden="true">✓</span>
                    </button>
                  );
                })}
              </div>

              <aside className="canvas-recharge-note">
                <strong>充值说明</strong>
                <span>支付成功后积分会自动到账，无需手动刷新。</span>
                <span>虚拟商品充值成功后不支持退款，请按需购买。</span>
              </aside>
            </section>

            <aside className="canvas-recharge-payment" aria-label="付款信息">
              <div className="canvas-recharge-payment-label">
                <span>02 / PAY</span>
                <strong>扫码完成支付</strong>
              </div>

              <div className="canvas-recharge-methods" role="group" aria-label="支付方式">
                {['wechat', 'alipay'].map((method) => (
                  <button
                    type="button"
                    className={`canvas-recharge-method${payMethod === method ? ' is-selected' : ''}`}
                    key={method}
                    aria-pressed={payMethod === method}
                    onClick={() => handleMethodSelect(method)}
                  >
                    <PaymentMark method={method} />
                    {method === 'wechat' ? '微信支付' : '支付宝'}
                  </button>
                ))}
              </div>

              <div className={`canvas-recharge-qr-shell is-${paymentStatus}`} aria-live="polite">
                {paymentStatus === 'success' ? (
                  <div className="canvas-recharge-payment-success">
                    <span aria-hidden="true">✓</span>
                    <strong>充值成功</strong>
                    <small>账户积分已更新</small>
                  </div>
                ) : isCreatingOrder ? (
                  <div className="canvas-recharge-qr-placeholder">
                    <span className="canvas-recharge-spinner" aria-hidden="true" />
                    <small>正在生成付款码</small>
                  </div>
                ) : qrCodeSrc ? (
                  <img src={qrCodeSrc} alt={`${payMethod === 'wechat' ? '微信' : '支付宝'}支付二维码`} />
                ) : (
                  <div className="canvas-recharge-qr-placeholder">
                    <span className="canvas-recharge-qr-symbol" aria-hidden="true">▦</span>
                    <small>{paymentStatus === 'expired' ? '付款码已过期' : '付款码生成失败'}</small>
                  </div>
                )}
              </div>

              <div className="canvas-recharge-payment-price">
                {hasDiscount && <del>¥{formatPrice(originalPrice)}</del>}
                <span>¥</span>
                <strong>{formatPrice(payablePrice)}</strong>
              </div>

              {hasDiscount && <p className="canvas-recharge-discount">已自动使用当前账户的最优优惠</p>}
              {paymentStatus === 'waiting' && <p className="canvas-recharge-waiting"><i /> 等待支付结果</p>}
              {paymentStatus === 'expired' && (
                <button
                  type="button"
                  className="canvas-recharge-retry"
                  onClick={() => createOrder(selectedPackage, payMethod)}
                >
                  重新生成付款码
                </button>
              )}
              {paymentStatus === 'error' && (
                <button
                  type="button"
                  className="canvas-recharge-retry"
                  onClick={() => createOrder(selectedPackage, payMethod)}
                >
                  重试生成付款码
                </button>
              )}

              {orderNo && paymentStatus !== 'success' && (
                <span className="canvas-recharge-order-number" title={orderNo}>订单号 {orderNo}</span>
              )}

              <Link className="canvas-recharge-back" to="/">
                返回灵感画布 <span aria-hidden="true">→</span>
              </Link>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}

export default PointsRechargePage;
